/**
 * Ingredient Card Component
 */

import { motion } from 'framer-motion';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import type { Ingredient } from '../../types/inventory';
import { CATEGORY_LABELS, STORAGE_LABELS } from '../../types/inventory';

interface IngredientCardProps {
  ingredient: Ingredient;
  onClick?: () => void;
}

export function IngredientCard({ ingredient, onClick }: IngredientCardProps) {
  const stockPercentage =
    ingredient.minimumStock > 0
      ? (ingredient.currentStock / ingredient.minimumStock) * 100
      : 100;

  const getStockColor = () => {
    if (ingredient.isCriticalStock) return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/20';
    if (ingredient.isLowStock) return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/20';
    return 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/20';
  };

  const getStockBarColor = () => {
    if (ingredient.isCriticalStock) return 'bg-red-500 dark:bg-red-400';
    if (ingredient.isLowStock) return 'bg-amber-500 dark:bg-amber-400';
    return 'bg-emerald-500 dark:bg-emerald-400';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      onClick={onClick}
      className="bg-white dark:bg-[#25252d] rounded-xl border border-gray-200 dark:border-white/[0.08] p-5 cursor-pointer hover:border-orange-200 dark:hover:border-orange-500/20 transition-all"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{ingredient.name}</h3>
          <p className="text-xs text-gray-500 dark:text-white/40">
            {CATEGORY_LABELS[ingredient.category]}
            {ingredient.storageLocation && (
              <span className="ml-2">• {STORAGE_LABELS[ingredient.storageLocation]}</span>
            )}
          </p>
        </div>
        {ingredient.allergen && (
          <span className="px-2 py-1 text-xs font-medium rounded-lg bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 flex items-center gap-1">
            <AlertCircle size={12} /> Allergene
          </span>
        )}
      </div>

      {/* Stock Level */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-white/60">Giacenza</span>
          <span className={`text-sm font-bold px-2 py-1 rounded-lg ${getStockColor()}`}>
            {ingredient.currentStock.toFixed(2)} {ingredient.unitOfMeasure}
          </span>
        </div>
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-white/[0.08] rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${getStockBarColor()}`}
            style={{ width: `${Math.min(Math.max(stockPercentage, 0), 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500 dark:text-white/40">
            Min: {ingredient.minimumStock.toFixed(2)} {ingredient.unitOfMeasure}
          </span>
          {ingredient.isLowStock && !ingredient.isCriticalStock && (
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle size={12} /> Scorta bassa
            </span>
          )}
          {ingredient.isCriticalStock && (
            <span className="text-xs font-medium text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertCircle size={12} /> Scorta critica
            </span>
          )}
        </div>
      </div>

      {/* Cost & Value */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
        <div>
          <p className="text-xs text-gray-500 dark:text-white/40 mb-1">Costo unitario</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{ingredient.unitCost.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-white/40 mb-1">Valore totale</p>
          <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            {ingredient.totalValue?.toFixed(2) || '0.00'}
          </p>
        </div>
      </div>

      {/* Supplier Info */}
      {ingredient.supplierName && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
          <p className="text-xs text-gray-500 dark:text-white/40">Fornitore</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{ingredient.supplierName}</p>
        </div>
      )}

      {/* Products Using */}
      {ingredient.productsUsingCount !== undefined && ingredient.productsUsingCount > 0 && (
        <div className="mt-3 flex items-center gap-1 text-xs text-gray-500 dark:text-white/40">
          <span>Usato in {ingredient.productsUsingCount} piatt{ingredient.productsUsingCount === 1 ? 'o' : 'i'}</span>
        </div>
      )}
    </motion.div>
  );
}
